import { Fragment, useCallback, useEffect, useMemo, useState } from 'react'
import { ALL_TIME, DateRange } from '../../lib/dateRange'
import ReportRangeFilter from './ReportRangeFilter'
import {
  CommissionReport,
  CommissionRow,
  calculateCommission,
  commissionReportCsv,
  loadCommissionReport,
  ReportCostLine,
} from '../../services/commissionReconService'
import { COST_TYPE_LABEL, CostType, directionOf, money as round2 } from '../../lib/commission'
import { overrideCostEstimate } from '../../services/standardCostService'
import { isSuperAdmin } from '../../lib/permissions'
import type { User } from '../../types'
import './CommissionReconView.css'
import { toast } from '../Toast'

/**
 * Commission Report — Winnie's build spec v2, section 7.
 *
 * Every Provisional/Final/Adjusted column and badge from v1 is gone, because in this model
 * there is no such thing as a provisional commission. What replaces them is a single honest
 * signal: how many of a vehicle's cost lines rest on an estimate rather than an invoice.
 *
 * That column is the delicate one, and the wording matters. It has to say "part of this
 * figure is an estimate" without implying "this figure will change" — because it will not.
 * Section 9: *"shows which vehicles' commission rests partly on an estimate, without implying
 * that figure is provisional or subject to change."* The badge is therefore neutral grey once
 * locked, not amber, and the drill-through spells the distinction out in words.
 *
 * The seventeenth column the spec does not ask for — variance against the platform's own net
 * margin — stays. The two definitions genuinely disagree, live it is exactly the buying fee
 * on all 153 sold vehicles, and a report that quietly picked one would recreate the problem
 * margin.ts was written to end.
 *
 * Money goes through toLocaleString, never toFixed: toFixed drops the thousands separator and
 * is how $18500.00 once appeared beside $18,500.00 on the same card.
 */

const money = (n: number | null | undefined) =>
  n === null || n === undefined
    ? '—'
    : `${n < 0 ? '-$' : '$'}${Math.abs(n).toLocaleString('en-AU', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`

const pct = (rate: number | null | undefined) =>
  rate === null || rate === undefined ? '—' : `${Number((rate * 100).toFixed(2))}%`

const dmy = (iso: string | null | undefined) => {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  return `${String(d.getUTCDate()).padStart(2, '0')}/${String(d.getUTCMonth() + 1).padStart(2, '0')}/${d.getUTCFullYear()}`
}

function StatusPill({ row }: { row: CommissionRow }) {
  if (row.isLocked) return <span className="com-pill ok">Locked</span>
  if (!row.gate.ok) return <span className="com-pill bad">Blocked</span>
  return <span className="com-pill info">Ready</span>
}

function Card({
  title,
  value,
  detail,
  tone,
}: {
  title: string
  value: string
  detail: string
  tone?: 'warn' | 'bad'
}) {
  return (
    <div className={`com-card ${tone || ''}`}>
      <div className="com-card-t">{title}</div>
      <div className="com-card-v">{value}</div>
      <div className="com-card-d">{detail}</div>
    </div>
  )
}

export default function CommissionReconView({ user }: { user?: User | null }) {
  const [range, setRange] = useState<DateRange>(ALL_TIME)
  const [report, setReport] = useState<CommissionReport | null>(null)
  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState<string | null>(null)
  const [search, setSearch] = useState('')
  const [onlyAttention, setOnlyAttention] = useState(false)
  const [open, setOpen] = useState<string | null>(null)
  const [note, setNote] = useState<string | null>(null)
  const [busy, setBusy] = useState<string | null>(null)

  const refresh = useCallback(async () => {
    setLoading(true)
    setErr(null)
    try {
      setReport(await loadCommissionReport(range))
    } catch (e) {
      setErr((e as Error).message)
    } finally {
      setLoading(false)
    }
  }, [range])

  useEffect(() => {
    void refresh()
  }, [refresh])

  const rows = useMemo(() => {
    const all = report ? report.rows : []
    const q = search.trim().toLowerCase()
    return all.filter((r) => {
      if (onlyAttention && r.warnings.length === 0 && r.gate.ok && !r.marginDisagrees) return false
      if (!q) return true
      return (
        r.vin.toLowerCase().includes(q) ||
        r.vehicle.toLowerCase().includes(q)
      )
    })
  }, [report, search, onlyAttention])

  const summary = report ? report.summary : null

  const exportCsv = () => {
    const blob = new Blob([commissionReportCsv(rows)], { type: 'text/csv;charset=utf-8' })
    const a = document.createElement('a')
    a.href = URL.createObjectURL(blob)
    a.download = `commission-report-${new Date().toISOString().slice(0, 10)}.csv`
    a.click()
    URL.revokeObjectURL(a.href)
  }

  async function strike(row: CommissionRow) {
    setBusy(row.dealId)
    setNote(null)
    try {
      const res = await calculateCommission(row.dealId)
      setNote(res.message)
      if (res.ok) {
        toast.saved()
        await refresh()
      }
    } catch (e) {
      setNote((e as Error).message)
    } finally {
      setBusy(null)
    }
  }

  return (
    <div className="com-page">
      <div className="com-head">
        <h1>Commission Report</h1>
        <p>
              Commission is calculated once, when the vehicle is sold &mdash; in transit or
              delivered &mdash; and does not move afterwards. A vehicle cannot be struck while a
              cost line has neither an invoice nor a standard rate: it is blocked and says so,
              rather than being calculated as though that cost were zero. Where a supplier
              invoice was missing at the lock point the cost was accrued at the standard rate
              &mdash; when the real invoice arrives it corrects the P&amp;L through an accrual
              reversal, not the commission.
        </p>
      </div>

      {err && <div className="com-banner bad">{err}</div>}
      {note && <div className="com-banner">{note}</div>}

      {report && report.totals.disagreeing > 0 && (
        <div className="com-banner warn">
          <b>
            {report.totals.disagreeing} vehicle{report.totals.disagreeing === 1 ? '' : 's'} where this
            report and the platform&rsquo;s own net margin disagree.
          </b>{' '}
          The platform subtracts a buying fee and this report does not. Neither figure has been
          changed — the variance column shows the gap so somebody can decide which is right.
        </div>
      )}

      {report && report.totals.blocked > 0 && (
        <div className="com-banner warn">
          <b>
            {report.totals.blocked} vehicle{report.totals.blocked === 1 ? '' : 's'} cannot have
            commission struck yet.
          </b>{' '}
          Each is missing either a sale price, or a cost with no invoice and no standard
          rate. Nothing has been accrued at $0 in its place, and no commission figure is shown
          against them &mdash; open a row to see which cost is missing.
        </div>
      )}

      {/* Every action on this screen sets a note and, until now, nothing showed it - so
          pressing Calculate or setting a cost by hand looked like nothing had happened
          whether it worked or was refused. */}
      {note && (
        <div className="com-banner" role="status">
          <span>{note}</span>
          <button className="com-btn" onClick={() => setNote(null)}>
            Dismiss
          </button>
        </div>
      )}

      <div className="com-cards">
          <Card
            title="Commission calculated"
            value={summary ? money(summary.totalCommission) : '—'}
            detail={`${rows.filter((r) => r.isLocked).length} vehicle${
              rows.filter((r) => r.isLocked).length === 1 ? '' : 's'
            } locked in this period`}
          />
        <Card
          title="Vehicles resting on an estimate"
          value={summary ? String(summary.vehiclesWithAccruals) : '—'}
          detail="One or more cost lines accrued, not invoiced"
        />
        <Card
          title="Unreversed accruals"
          value={summary ? money(summary.unreversedAccrualValue) : '—'}
          detail="Still waiting on a real supplier invoice"
          tone={summary && summary.unreversedAccrualValue > 0 ? 'warn' : undefined}
        />
        <Card
          title="Blocked"
          value={report ? String(report.totals.blocked) : '—'}
            detail="No sale price, or a cost with neither an invoice nor a standard rate"
          tone={report && report.totals.blocked > 0 ? 'bad' : undefined}
        />
      </div>

      <ReportRangeFilter value={range} onChange={setRange} />

      <div className="com-toolbar">
        <input
          className="com-search"
          placeholder="Search VIN or vehicle"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <label className="com-check">
          <input
            type="checkbox"
            checked={onlyAttention}
            onChange={(e) => setOnlyAttention(e.target.checked)}
          />
          Only rows needing attention
        </label>
        <button className="com-btn" onClick={exportCsv} disabled={!rows.length}>
          Export CSV
        </button>
      </div>

      <div className="com-tablewrap">
        <table className="com-table">
          <thead>
            <tr>
              <th>VIN</th>
              <th>Vehicle</th>
              <th className="num">Sale price</th>
              <th className="num">Landed cost</th>
              <th className="num">Net margin</th>
              <th className="num">Comm %</th>
              <th className="num">Commission</th>
              <th>Calculated</th>
              <th className="num">Unconfirmed</th>
              <th>Status</th>
              <th className="num">vs platform</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={12} className="com-none">
                  Loading…
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={12} className="com-none">
                  No sold vehicles in this period.
                </td>
              </tr>
            ) : (
              rows.map((row) => (
                <Row
                  key={row.dealId}
                  row={row}
                  open={open === row.dealId}
                  busy={busy === row.dealId}
                  onToggle={() => setOpen(open === row.dealId ? null : row.dealId)}
                  onStrike={() => strike(row)}
                  canOverride={isSuperAdmin(user)}
                  onOverride={async (costType, amount, reason) => {
                    const r = await overrideCostEstimate({
                      dealId: row.dealId,
                      costType,
                      amount,
                      reason,
                      user: user || null,
                    })
                    setNote(r.message)
                    if (r.ok) await refresh()
                    return { ok: r.ok, message: r.message }
                  }}
                />
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}

type GroupKey = 'Inflow' | 'Outflow' | 'Unclassified'

interface LineGroup {
  key: GroupKey
  heading: string
  note: string
  lines: ReportCostLine[]
  subtotal: number
}

const GROUP_ORDER: GroupKey[] = ['Inflow', 'Outflow', 'Unclassified']

const GROUP_TEXT: Record<GroupKey, { heading: string; note: string }> = {
  Inflow: { heading: 'Inflow', note: 'what it cost to get the car in' },
  Outflow: { heading: 'Outflow', note: 'what the sale cost' },
  Unclassified: {
    heading: 'Not classified',
    note: 'no cost type on the line, so neither side can claim it',
  },
}

/**
 * Landed cost, split into the two sides of the deal. Winnie, 10 Sep: yes to an
 * inflow/outflow drill-down.
 *
 * Nothing is added or dropped here - every line still sits in the same total, and the
 * Landed cost row underneath is read straight off the row rather than re-summed, so the
 * groups cannot quietly disagree with the figure in the table above. A line with no cost
 * type gets its own heading instead of being pushed onto a side, because a guess there
 * moves real money from one half of the margin to the other.
 */
function groupLines(lines: ReportCostLine[]): LineGroup[] {
  const buckets: Record<GroupKey, ReportCostLine[]> = {
    Inflow: [],
    Outflow: [],
    Unclassified: [],
  }
  for (const line of lines) {
    const side = directionOf(line.costType)
    buckets[side === 'Inflow' || side === 'Outflow' ? side : 'Unclassified'].push(line)
  }
  const groups: LineGroup[] = []
  for (const key of GROUP_ORDER) {
    const group = buckets[key]
    if (group.length === 0) continue
    groups.push({
      key,
      heading: GROUP_TEXT[key].heading,
      note: GROUP_TEXT[key].note,
      lines: group,
      subtotal: round2(group.reduce((sum, l) => sum + (Number(l.amount) || 0), 0)),
    })
  }
  return groups
}

function Row({
  row,
  open,
  busy,
  onToggle,
  onStrike,
  canOverride,
  onOverride,
}: {
  row: CommissionRow
  open: boolean
  busy: boolean
  onToggle: () => void
  onStrike: () => void
  canOverride: boolean
  onOverride: (
    costType: CostType,
    amount: number,
    reason: string,
  ) => Promise<{ ok: boolean; message: string }>
}) {
  const attention = row.warnings.length > 0

  return (
    <>
      <tr className={attention ? 'com-attention' : ''} onClick={onToggle}>
        <td className="mono">
          <span className="com-caret">{open ? '▾' : '▸'}</span> {row.vin || '—'}
        </td>
        <td>{row.vehicle}</td>
        <td className="num">{money(row.salePrice)}</td>
        <td className="num">{money(row.landedCost)}</td>
        <td className={`num ${row.netMargin !== null && row.netMargin < 0 ? 'com-alarm' : ''}`}>
          {money(row.netMargin)}
        </td>
        <td className="num">{pct(row.commissionPct)}</td>
        <td className="num">
          {row.isLocked || row.gate.ok ? money(row.commissionAmount) : '\u2014'}
        </td>
        <td>{dmy(row.calculatedAt)}</td>
        <td className="num">
          {row.estimatedLines === 0 && row.missingBills === 0 ? (
            '—'
          ) : (
            <>
              {/* An estimate is a number somebody stands behind. A missing bill is a cost
                  we know is coming and have not priced. Never the same pill. */}
              {row.estimatedLines > 0 && (
                <span className="com-pill estimated">{row.estimatedLines} est</span>
              )}
              {row.missingBills > 0 && (
                <span className="com-pill bad">{row.missingBills} missing</span>
              )}
            </>
          )}
        </td>
        <td>
          <StatusPill row={row} />
        </td>
        <td className={`num ${row.marginDisagrees ? 'com-alarm' : ''}`}>
          {row.marginVariance === null ? '—' : money(row.marginVariance)}
        </td>
        <td className="com-actions">
          {!row.isLocked && row.gate.ok && (
            <button
              className="com-btn primary"
              disabled={busy}
              onClick={(e) => {
                e.stopPropagation()
                onStrike()
              }}
            >
              {busy ? '…' : 'Calculate'}
            </button>
          )}
        </td>
      </tr>
      {open && (
        <tr className="com-detail">
          <td colSpan={12}>
            {attention && (
              <ul className="com-warnings">
                {row.warnings.map((w, i) => (
                  <li key={i}>{w}</li>
                ))}
              </ul>
            )}

            {row.estimatedLines > 0 && (
              <div className="com-pending-note">
                {row.estimatedLines} of this vehicle&rsquo;s cost lines {row.estimatedLines === 1 ? 'is' : 'are'}{' '}
                an accrued estimate rather than a supplier invoice.{' '}
                {row.isLocked
                  ? 'The commission below is still final — when the real invoice arrives it reverses the accrual through the P&L and this figure does not change.'
                  : 'Any line marked “Will be accrued” is not posted yet; pressing Calculate writes it at the standard rate and strikes the commission on the total shown here, so this figure will not move when you do.'}
              </div>
            )}

            {!row.gate.ok && row.gate.code !== 'already_locked' && (
              <>
                <div className="com-pending-note">{row.gate.reason}</div>
                {canOverride && !row.isLocked && row.blockedCosts.length > 0 && (
                  <OverrideBlocked costs={row.blockedCosts} onOverride={onOverride} />
                )}
              </>

            )}

            <div className="com-detail-grid">
              <div>
                <span className="com-dl">Sold</span>
                <span className="com-dv">{dmy(row.soldOn)}</span>
              </div>
              <div>
                <span className="com-dl">Calculated</span>
                <span className="com-dv">{dmy(row.calculatedAt)}</span>
              </div>
              <div>
                <span className="com-dl">Platform net margin</span>
                <span className="com-dv">{money(row.platformNetMargin)}</span>
              </div>
              <div>
                <span className="com-dl">This report</span>
                <span className="com-dv">{money(row.netMargin)}</span>
              </div>
              <div>
                <span className="com-dl">Unreversed accruals</span>
                <span className="com-dv">{money(row.unreversedAccruals)}</span>
              </div>
            </div>

            <table className="com-lines">
              <thead>
                <tr>
                  <th>Cost</th>
                  <th>Type</th>
                  <th>Supplier</th>
                  <th>Invoice #</th>
                  <th className="num">Amount</th>
                  <th className="num">Accrued at</th>
                  <th>Bill</th>
                  <th>Source</th>
                </tr>
              </thead>
              <tbody>
                {row.lines.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="com-none">
                      No costs recorded against this vehicle at all.
                    </td>
                  </tr>
                ) : (
                  groupLines(row.lines).map((group) => (
                    <Fragment key={group.key}>
                      <tr className="com-group">
                        <td colSpan={8}>
                          {group.heading}
                          <span className="com-group-note"> — {group.note}</span>
                        </td>
                      </tr>
                      {group.lines.map((l) => (
                    <tr key={l.id} className={l.costType ? '' : 'com-unclassified'}>
                      <td>{l.label}</td>
                      <td>{l.costType || <em>no cost type</em>}</td>
                      <td>{l.supplier || '—'}</td>
                      <td className="mono">{l.invoiceRef || '—'}</td>
                      <td className="num">{money(l.amount)}</td>
                      <td className="num">{l.estimatedAmount === null ? '—' : money(l.estimatedAmount)}</td>
                      <td>
                        <span className={`com-pill ${l.billStatus === 'Received' ? 'ok' : l.billStatus === 'Missing' ? 'bad' : 'estimated'}`}>
                          {l.billStatus}
                        </span>
                      </td>
                      <td>
                        <span className={`com-src ${l.source}`}>
                          {l.source === 'expense'
                            ? 'Cost line'
                            : l.source === 'deal'
                              ? 'Deal field'
                              : 'Will be accrued'}
                        </span>
                      </td>
                    </tr>
                      ))}
                      <tr className="com-subtotal">
                        <td colSpan={4}>{group.heading} subtotal</td>
                        <td className="num">{money(group.subtotal)}</td>
                        <td colSpan={3} />
                      </tr>
                    </Fragment>
                  ))
                )}
                {row.lines.length > 0 && (
                  <tr className="com-landed">
                    <td colSpan={4}>Landed cost</td>
                    <td className="num">{money(row.landedCost)}</td>
                    <td colSpan={3} />
                  </tr>
                )}
              </tbody>
            </table>
          </td>
        </tr>
      )}
    </>
  )
}


/**
 * A super admin puts a figure against the cost that is holding this vehicle up.
 *
 * Nick, 12 Sep: freight has to count towards commission, and the super admin can override
 * it. The report already names the blocking cost, so the answer belongs here - sending
 * somebody off to Cost of Sales to find the car and guess which cost type to add is how a
 * 153-vehicle backlog stays a backlog.
 *
 * The reason is not decoration. With no invoice and no rate card, the reason is the whole
 * defence of the number, and it is what an auditor reads a year from now.
 */
function OverrideBlocked({
  costs,
  onOverride,
}: {
  costs: CostType[]
  onOverride: (
    costType: CostType,
    amount: number,
    reason: string,
  ) => Promise<{ ok: boolean; message: string }>
}) {
  const offerable = costs.filter((c) => c !== 'PurchasePrice')
  const noPurchasePrice = costs.indexOf('PurchasePrice') >= 0

  const [costType, setCostType] = useState<CostType | ''>(offerable[0] || '')
  const [amount, setAmount] = useState('')
  const [reason, setReason] = useState('')
  const [saving, setSaving] = useState(false)

  const typed = amount.trim()
  const amountOk = typed !== '' && Number.isFinite(Number(typed)) && Number(typed) >= 0
  const ready = Boolean(costType) && amountOk && reason.trim().length >= 3 && !saving

  const [outcome, setOutcome] = useState<string | null>(null)

  const submit = async () => {
    if (!ready || !costType) return
    setSaving(true)
    setOutcome(null)
    try {
      const r = await onOverride(costType as CostType, Number(typed), reason.trim())
      if (r.ok) {
        setAmount('')
        setReason('')
        toast.saved()
      }
      /* Say what happened here, on the line, rather than somewhere the person is not
         looking. A refusal that lands off screen reads as nothing having happened. */
      setOutcome(r.message)
    } catch (e) {
      /* Anything that gets this far is a surprise, and a surprise that leaves the button
         reading "Saving..." for ever is worse than the surprise itself. */
      setOutcome('That did not save: ' + ((e as Error).message || 'something went wrong') + '.')
    } finally {
      setSaving(false)
    }
  }

  if (!offerable.length) {
    return noPurchasePrice ? (
      <div className="com-pending-note">
        There is no purchase price on this vehicle. That is an incomplete deal rather than a
        late bill, so it is not something to set by hand here - put the purchase price on the
        deal and the vehicle will clear.
      </div>
    ) : null
  }

  return (
    <div className="com-override">
      <div className="com-override-head">
        Set the missing cost by hand
        <span className="com-override-sub">
          No invoice and no standard rate, so nothing can be accrued on its own. As a super
          admin you can put the figure in; it goes into landed cost like any other estimate,
          and when the real invoice arrives it reverses through the P&amp;L and leaves the
          commission alone.
        </span>
      </div>

      <div className="com-override-row">
        <select
          className="com-override-select"
          value={costType}
          onChange={(e) => setCostType(e.target.value as CostType)}
        >
          {offerable.map((c) => (
            <option key={c} value={c}>
              {COST_TYPE_LABEL[c] || c}
            </option>
          ))}
        </select>

        <input
          className="com-override-input"
          inputMode="decimal"
          placeholder="Amount"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
        />

        <input
          className="com-override-input com-override-reason"
          placeholder="Why this figure — quoted rate, carrier's email, agreed with the dealer…"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
        />

        <button className="com-btn primary" disabled={!ready} onClick={submit}>
          {saving ? 'Saving…' : 'Set and unblock'}
        </button>
      </div>

      {!ready && !saving && (
        <div className="com-override-hint">
          {!amountOk
            ? 'Enter the amount as a number.'
            : 'Say why this figure is the right one — it is what the number rests on instead of an invoice.'}
        </div>
      )}

      {outcome && <div className="com-override-hint">{outcome}</div>}

      {noPurchasePrice && (
        <div className="com-override-hint">
          This vehicle is also missing its purchase price, which is not something to set here.
          It needs to go on the deal.
        </div>
      )}
    </div>
  )
}
